import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { FileResponse } from "@/javascript/conduction-api";
import { Project } from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import ConductionModal from "@/components/modals/ConductionModal";
import { Button, Stack, Text } from "@mantine/core";
import { IconAlertCircle, IconCheck, IconFileMusic, IconFileTypePdf, IconFileTypeXml, IconFileTypeZip } from "@tabler/icons-react";
import { useContext, useState } from "react";

const ExportTypes = {
    MusicXML: "MusicXML",
    PDF: "PDF",
    MP3: "MP3",
    ZIP: "ZIP",
};

interface ExportMenuProps {
    onClose: () => void;
}

const ExportMenu: React.FC<ExportMenuProps> = ({ onClose }) => {
    const [exportingType, setExportingType] = useState<string | null>(null);
    const [exportedFormats, setExportedFormats] = useState<Set<string>>(new Set());

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("ProjectSelect must be a child of AuthContext.");
    }
    const { project, token } = auth;
    if (token === null) {
        return;
    }

    const handleExport = async (exportType: string) => {
        setExportingType(exportType);
        const notificationId = Utils.displayNotification(
            "Exporting...",
            "Your download will begin once the export process completes. This may take a few minutes.",
            "blue",
            "loading"
        );

        let fileExtension: string;
        let exportMethod: (project: Project, token: string) => Promise<FileResponse>;

        switch (exportType) {
            case ExportTypes.MusicXML:
                fileExtension = "musicxml";
                exportMethod = ConductionAPI.exportProjectToMusicXml;
                break;
            case ExportTypes.MP3:
                fileExtension = "mp3";
                exportMethod = ConductionAPI.exportProjectToMp3;
                break;
            case ExportTypes.PDF:
                fileExtension = "pdf";
                exportMethod = ConductionAPI.exportProjectToPdf;
                break;
            case ExportTypes.ZIP:
                fileExtension = "zip";
                exportMethod = ConductionAPI.exportProjectToZip;
                break;
            default:
                Utils.updateNotification(
                    notificationId,
                    "Export Error",
                    `Invalid export type: ${exportType}`,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
                setExportingType(null);
                return;
        }

        if (project === null) {
            Utils.updateNotification(
                notificationId,
                "You must have an active project open in order to export.",
                undefined,
                "red",
                <IconAlertCircle size={16} />,
                true
            );
            return;
        }

        try {
            const response = await exportMethod(project, token);

            if (!response.success) {
                throw new Error(response.message);
            }

            const link = document.createElement("a");
            link.href = response.fileBlobUrl;
            link.download = `${project.getName()}.${fileExtension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(response.fileBlobUrl);

            Utils.updateNotification(
                notificationId,
                "Export Complete",
                `The generated ${exportType} file will now download.`,
                "green",
                <IconCheck size={16} />
            );

            setExportedFormats((prev) => new Set(prev).add(exportType));
        } catch (error) {
            console.error(error);
            Utils.updateNotification(
                notificationId,
                "Export Failed",
                "An error occurred while exporting. Please try again later.",
                "red",
                <IconAlertCircle size={16} />,
                true
            );
        } finally {
            setExportingType(null);
        }
    };

    return (
        <ConductionModal
            title={"Export"}
            size={"lg"}
            onClose={onClose}
        >
            <Stack
                align="center"
                justify="center"
            >
                <Text size="xl" fw={700}>
                    How would you like to export your project?
                </Text>

                <Button
                    leftSection={<IconFileMusic size={20} />}
                    onClick={() => handleExport(ExportTypes.MP3)}
                    loading={exportingType === ExportTypes.MP3}
                    disabled={exportedFormats.has(ExportTypes.MP3)}
                >
                    Export MP3
                </Button>

                <Button
                    leftSection={<IconFileTypeXml size={20} />}
                    onClick={() => handleExport(ExportTypes.MusicXML)}
                    loading={exportingType === ExportTypes.MusicXML}
                    disabled={exportedFormats.has(ExportTypes.MusicXML)}
                >
                    Export MusicXML
                </Button>

                <Button
                    leftSection={<IconFileTypePdf size={20} />}
                    onClick={() => handleExport(ExportTypes.PDF)}
                    loading={exportingType === ExportTypes.PDF}
                    disabled={exportedFormats.has(ExportTypes.PDF)}
                >
                    Export Score PDF
                </Button>

                <Button
                    leftSection={<IconFileTypeZip size={20} />}
                    onClick={() => handleExport(ExportTypes.ZIP)}
                    loading={exportingType === ExportTypes.ZIP}
                    disabled={exportedFormats.has(ExportTypes.ZIP)}
                >
                    Export Instrument Part PDFs
                </Button>
            </Stack>
        </ConductionModal>
    );
};

export default ExportMenu;
